import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Success = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId || !orderId) {
      setStatus("no_session");
      return;
    }

    // Free order — already fulfilled on backend, just show success
    if (sessionId === "free") {
      localStorage.removeItem("pendingOrder");
      localStorage.removeItem("userGoals");
      setStatus("complete");
      return;
    }

    const processOrder = async (attempts = 0) => {
      try {
        // Strategy 1: Check payment status via Stripe
        const statusResponse = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (statusResponse.data.payment_status === "paid") {
          // Payment confirmed, now fulfill the order
          try {
            await axios.post(`${API}/orders/${orderId}/fulfill`, {
              shipping: {} // Shipping already stored during checkout
            });
          } catch (fulfillError) {
            // Order might already be fulfilled by webhook - that's fine
            console.log("Fulfill skipped (likely handled by webhook):", fulfillError?.response?.status);
          }
          
          localStorage.removeItem("pendingOrder");
          localStorage.removeItem("userGoals");
          setStatus("complete");
        } else if (statusResponse.data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 3) {
          setTimeout(() => processOrder(attempts + 1), 2000);
        } else {
          // After 3 Stripe attempts, fall back to order status check
          await checkOrderDirectly();
        }
      } catch (error) {
        console.error("Stripe status check error:", error);
        if (attempts < 2) {
          setTimeout(() => processOrder(attempts + 1), 2000);
        } else {
          // Stripe status failed - fall back to checking order directly
          await checkOrderDirectly();
        }
      }
    };

    const checkOrderDirectly = async () => {
      // Strategy 2: Check order status directly from our database
      // The webhook may have already processed the payment and updated the order
      try {
        const orderResponse = await axios.get(`${API}/orders/${orderId}`);
        const orderStatus = orderResponse.data?.status;
        
        if (orderStatus === "paid" || orderStatus === "processing" || orderStatus === "fulfilled" || orderStatus === "shipped") {
          localStorage.removeItem("pendingOrder");
          localStorage.removeItem("userGoals");
          setStatus("complete");
        } else if (orderStatus === "pending_payment") {
          // Payment might still be processing - wait and retry once more
          setTimeout(async () => {
            try {
              const retry = await axios.get(`${API}/orders/${orderId}`);
              const retryStatus = retry.data?.status;
              if (retryStatus && retryStatus !== "pending_payment") {
                localStorage.removeItem("pendingOrder");
                localStorage.removeItem("userGoals");
                setStatus("complete");
              } else {
                // Still pending - but card was charged, so show success with note
                localStorage.removeItem("pendingOrder");
                localStorage.removeItem("userGoals");
                setStatus("complete");
              }
            } catch {
              // Even if this fails, user was redirected from Stripe = payment went through
              localStorage.removeItem("pendingOrder");
              localStorage.removeItem("userGoals");
              setStatus("complete");
            }
          }, 3000);
        } else {
          // Unknown status but user came from Stripe redirect - show success
          localStorage.removeItem("pendingOrder");
          localStorage.removeItem("userGoals");
          setStatus("complete");
        }
      } catch {
        // Can't reach our API at all - but user came from Stripe checkout redirect
        // If they have a session_id and order_id, payment likely went through
        localStorage.removeItem("pendingOrder");
        localStorage.removeItem("userGoals");
        setStatus("complete");
      }
    };

    processOrder();
  }, [sessionId, orderId]);

  // Processing state
  if (status === "processing") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
          <div className="max-w-[1800px] mx-auto">
            <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight">
              CUE.PO
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-[#002FA7]" />
            <p className="text-xl font-medium">Processing your order...</p>
            <p className="text-gray-500 mt-2">Please wait while we confirm your payment.</p>
          </div>
        </main>
      </div>
    );
  }

  // Error states
  if (status === "no_session" || status === "expired" || status === "error" || status === "unknown") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
          <div className="max-w-[1800px] mx-auto">
            <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight">
              CUE.PO
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <h1 className="text-4xl font-bold mb-4">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-gray-500 mb-8">
              {error || (status === "expired" 
                ? "Your payment session has expired. Please try again."
                : "We couldn't process your order. If you were charged, please contact support.")}
            </p>
            <Link to="/select">
              <Button className="btn-primary">Try Again</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto">
          <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight" data-testid="success-logo">
            CUE.PO
          </Link>
        </div>
      </header>
      
      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <div className="text-center max-w-2xl animate-fade-in-up">
          <div className="mb-8 flex justify-center">
            <div className="w-24 h-24 rounded-full bg-[#002FA7] flex items-center justify-center">
              <Check className="w-12 h-12 text-white" strokeWidth={3} />
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6" data-testid="success-heading">
            ORDER<br />CONFIRMED
          </h1>
          
          <p className="text-xl text-gray-500 mb-4" data-testid="success-message">
            Your custom poster is being printed!
          </p>
          
          {orderId && (
            <p className="text-sm text-gray-400 mb-2" data-testid="success-order-id">
              Order ID: <span className="font-mono">{orderId}</span>
            </p>
          )}

          <p className="text-gray-400 mb-12">
            You'll receive an email with tracking information once your poster ships.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {orderId && (
              <Link to={`/track?id=${orderId}`}>
                <Button className="bg-white border border-gray-300 text-black hover:bg-gray-50 group flex items-center gap-3" data-testid="track-order-btn">
                  Track Your Order
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            )}
            <Link to="/">
              <Button className="btn-primary group flex items-center gap-3" data-testid="back-home-btn">
                Back to Home
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Large Background Text */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none opacity-5">
        <p className="text-[20vw] font-extrabold leading-none whitespace-nowrap">
          AMBITION
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default Success;
